import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { cn } from "@/core/shared/v2/lib/utils";
import { Badge } from "@/core/shared/v2/ui/badge";
import { Button } from "@/core/shared/v2/ui/button";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import { DistributionNotFound } from "../../components/distribution-not-found";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import { viewOfferListEvent } from "../../tracking/events";

const configOrder = ["free_packages", "purchase_products", "friends_and_family"];

export function SelectDistributionContent() {
  const { t } = useTranslation();
  const history = useHistory();
  const { voucherDistribution } = useVouchersDistributionState();

  const sortedConfigs = useMemo(() => {
    if (!voucherDistribution) return [];
    return [...voucherDistribution.configs].sort((a, b) => {
      const indexA = configOrder.indexOf(a.configName);
      const indexB = configOrder.indexOf(b.configName);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  }, [voucherDistribution]);

  const basenamePrefix = voucherDistribution ? `/pp/${voucherDistribution.slug}` : "";
  const toRelative = (absolutePath: string) => absolutePath.replace(basenamePrefix, "") || "/";

  // Auto-redirect when only a single distribution is available
  // biome-ignore lint/correctness/useExhaustiveDependencies: history/basenamePrefix derived from distribution, stable for the lifetime of the app
  useEffect(() => {
    if (sortedConfigs.length === 1) {
      history.replace(toRelative(sortedConfigs[0].path));
    }
  }, [sortedConfigs]);

  // REV-63: Fire `view_offer_list` once on landing
  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once per distribution when the selection list is shown
  useEffect(() => {
    if (!voucherDistribution || sortedConfigs.length <= 1) return;
    viewOfferListEvent({
      partnerName: voucherDistribution.name,
      availableOffers: sortedConfigs.map((c) => c.configName).join(","),
    });
  }, [voucherDistribution?.slug]);

  if (sortedConfigs.length === 1) return null;
  if (sortedConfigs.length === 0) return <DistributionNotFound />;

  return (
    <IntersectionFadeIn
      className={cn(["mx-auto w-full max-w-[1176px] px-4 pb-14 max-lg:pt-8 md:px-8 md:pb-20 lg:px-12"])}
    >
      <div className={cn("flex flex-col items-center lg:mb-6")}>
        <h1 className="text-center font-serif text-3xl lg:text-5xl">{t(`cmsTranslations.landing_page.title`)}</h1>
        <p className="mt-2 max-w-3xl text-pretty text-center text-sm lg:mt-4 lg:text-xl">
          {t(`cmsTranslations.landing_page.description`)}
        </p>
      </div>
      <div
        className={cn("gap-4 py-4 max-lg:flex max-lg:flex-col lg:gap-6", {
          "grid grid-cols-2": sortedConfigs.length === 2,
          "grid grid-cols-3": sortedConfigs.length >= 3,
        })}
      >
        {sortedConfigs.map((config) => (
          <button
            key={config.configName}
            type="button"
            disabled={config.limitReached}
            className={cn(
              "group flex h-full flex-col overflow-hidden rounded-lg border-[3px] border-transparent bg-white text-left shadow-lg transition-all duration-300 ease-out",
              config.limitReached ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-dodger-blue-700",
            )}
            onClick={() => history.push(toRelative(config.path))}
          >
            <div className="relative h-64 w-full shrink-0 overflow-hidden">
              <img
                src={config.image}
                alt={config.title}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-1 flex-col items-center justify-between space-y-6 p-6 text-center">
              <div className="flex flex-col items-center space-y-4">
                {config.limitReached && (
                  <Badge variant="whiteLinen" size="sm">
                    {t("VOUCHERS_DISTRIBUTION.ERRORS.DISTRIBUTION_CONFIG_LIMIT_EXCEEDED")}
                  </Badge>
                )}
                <h3 className="font-semibold font-serif text-2xl text-foreground">{config.title}</h3>
                <p className="max-w-sm text-balance text-gray-600">{config.description}</p>
              </div>
              <Button className="mt-auto w-full sm:w-auto" variant="primary" size="xl">
                {config.buttonLabel}
              </Button>
            </div>
          </button>
        ))}
      </div>
    </IntersectionFadeIn>
  );
}
